import React, { useReducer, useCallback } from "react";
import createUseContext from "constate";
import validator from "validator";

interface AuthenticationState {
  contextEmail: string;
  canShowCallbackError: boolean;
}

const defaultContextEmail = "";
const defaultCanShowCallbackError = true;

const AuthenticationStateInitialState: AuthenticationState = {
  contextEmail: defaultContextEmail,
  canShowCallbackError: defaultCanShowCallbackError
};

enum AuthenticationActionType {
  SetEmail,
  CanShowCallbackError
}

type AuthenticationStateActionType = {
  type: AuthenticationActionType;
  contextEmail?: string;
  contextEmailValidationCallback?: null | ValidationCallback;
  canShowCallbackError?: boolean;
};

const IsEmailValid = (contextEmail: string): boolean => {
  if (contextEmail && contextEmail.length > 0) {
    let e = contextEmail.trim();
    if (e.length > 0) {
      return validator.isEmail(contextEmail);
    }
  }

  return false;
};

export type ValidationCallback = (result: boolean) => void;

function AuthenticationStateReducer (
  state: AuthenticationState,
  action: AuthenticationStateActionType
): AuthenticationState {
  switch (action.type) {
    case AuthenticationActionType.SetEmail:
      return {
        ...state,
        contextEmail: action.contextEmail || defaultContextEmail
      };
    case AuthenticationActionType.CanShowCallbackError:
      return {
        ...state,
        canShowCallbackError: action.canShowCallbackError || false
      };
    default:
      return state;
  }
}

const useAuthenticationInfo = () => {
  const [state, dispatch] = useReducer(
    AuthenticationStateReducer,
    AuthenticationStateInitialState
  );
  const { contextEmail, canShowCallbackError } = state;

  const setContextEmail = useCallback(
    (email: string, isValidCallback: null | ValidationCallback = null) => {
      if (IsEmailValid(email)) {
        if (isValidCallback) {
          isValidCallback(true);
        }

        dispatch({
          type: AuthenticationActionType.SetEmail,
          contextEmail: email,
          contextEmailValidationCallback: isValidCallback
        });
      } else {
        if (isValidCallback) {
          isValidCallback(false);
        }
      }
    },
    []
  );

  const validateEmail = (contextEmail: string): boolean => {
    return IsEmailValid(contextEmail);
  };

  const disableVisualizationOfCallbackError = useCallback(() => {
    dispatch({
      type: AuthenticationActionType.CanShowCallbackError,
      canShowCallbackError: false
    });
  }, []);

  return {
    contextEmail,
    canShowCallbackError,
    validateEmail,
    setContextEmail,
    disableVisualizationOfCallbackError
  };
};

const useAuthenticationContext = createUseContext(useAuthenticationInfo);
const AuthenticationContext = useAuthenticationContext.Context;

interface SharedScreensProps {
  children: React.ReactNode;
}

function AuthenticationInfoProvider (props: SharedScreensProps) {
  const value = useAuthenticationInfo();
  return (
    <AuthenticationContext.Provider value={value}>
      {props.children}
    </AuthenticationContext.Provider>
  );
}

export { useAuthenticationContext, AuthenticationInfoProvider };
